'use client'

import React, { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import { ChevronDown, ArrowRight } from 'lucide-react'

const MenuItem = ({ item, isActive = false, setActiveSubmenu = () => {} }) => {
  const [hovered, setHovered] = useState(false)
  const menuRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState({ left: '50%', transform: 'translateX(-50%)' })

  // Handle hover states
  const handleMouseEnter = () => {
    setHovered(true)
    if (item.sections) {
      setActiveSubmenu(true)
    }
  }

  const handleMouseLeave = () => {
    setHovered(false)
    if (item.sections) {
      setActiveSubmenu(false)
    }
  }

  // Calculate dropdown position to prevent it from going outside the screen
  useEffect(() => {
    if (menuRef.current && item.sections) {
      const updatePosition = () => {
        const menuRect = menuRef.current.getBoundingClientRect()
        const dropdownWidth = parseInt(item.cardWidth || '600')
        const viewportWidth = window.innerWidth

        // Default centered position
        let leftPosition = '50%'
        let transform = 'translateX(-50%)'

        // Check if dropdown would go outside the left edge
        if (menuRect.left - dropdownWidth / 2 < 20) {
          // Align to left edge of viewport with some padding
          leftPosition = `${Math.max(20 - menuRect.left, 0)}px`
          transform = 'none'
        }

        // Check if dropdown would go outside the right edge
        if (menuRect.left + dropdownWidth / 2 > viewportWidth - 20) {
          // Align to right edge of viewport with some padding
          const rightOffset = Math.max(menuRect.left + dropdownWidth / 2 - (viewportWidth - 20), 0)
          leftPosition = `calc(50% - ${rightOffset}px)`
        }

        setDropdownPosition({ left: leftPosition, transform })
      }

      updatePosition()
      window.addEventListener('resize', updatePosition)
      return () => window.removeEventListener('resize', updatePosition)
    }
  }, [item.sections, item.cardWidth, hovered, isActive])

  return (
    <div className="relative group" ref={menuRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link
        href={item.href || '#'}
        className="text-theme-header-text hover:text-theme-primary transition-colors font-medium flex items-center py-2 px-1"
      >
        {item.icon && React.createElement(item.icon, { className: 'w-4 h-4 mr-1' })}
        <span className="whitespace-nowrap text-sm 2xl:text-sm">{item.label}</span>
        {item.sections && (
          <ChevronDown
            className={`ml-1 w-3.5 h-3.5 transition-transform duration-200 ${isActive || hovered ? 'rotate-180' : ''}`}
          />
        )}
      </Link>

      {/* Dropdown Menu Card */}
      {item.sections && (
        <div
          className={`absolute ${isActive ? 'block' : 'hidden'} group-hover:block pt-4 z-20 w-auto`}
          style={{
            width: item.cardWidth || '600px',
            maxWidth: 'calc(100vw - 40px)',
            left: dropdownPosition.left,
            transform: dropdownPosition.transform,
          }}
        >
          <div className="bg-white shadow-xl rounded-lg border border-theme-border overflow-hidden backdrop-blur-lg backdrop-filter">
            <div
              className={`grid ${item.sections.length === 1 ? 'grid-cols-1' : item.sections.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} lg:grid-cols-${item.sections.length}`}
            >
              {item.sections.map((section, sectionIndex) => (
                <div
                  key={`section-${sectionIndex}`}
                  className={`p-5 lg:p-6 ${sectionIndex < item.sections.length - 1 ? 'border-b lg:border-b-0 lg:border-r border-theme-border' : ''} ${section.background ? section.background : ''}`}
                >
                  <h3 className="font-semibold text-sm mb-3 text-theme-primary uppercase tracking-wider">
                    {section.title}
                  </h3>
                  <div className="space-y-3.5">
                    {section.links &&
                      section.links.map((link, linkIndex) => (
                        <Link
                          key={`link-${sectionIndex}-${linkIndex}`}
                          href={link.href}
                          className="group/item flex items-start hover:bg-gray-50 rounded-md p-1.5 -ml-1.5 transition-colors duration-150"
                        >
                          {link.icon && (
                            <div className="mr-3 mt-0.5 flex-shrink-0">
                              {React.createElement(link.icon, {
                                className: `w-5 h-5 ${link.iconColor || 'text-theme-text-muted'} group-hover/item:text-theme-primary transition-colors`,
                              })}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm text-theme-text group-hover/item:text-theme-primary transition-colors">
                              {link.label}
                            </div>
                            {link.description && (
                              <div className="text-sm text-theme-text-muted mt-0.5 line-clamp-2">
                                {link.description}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                  </div>

                  {/* Featured items */}
                  {section.featured &&
                    section.featured.map((feature, featureIndex) => (
                      <div
                        key={`feature-${sectionIndex}-${featureIndex}`}
                        className={`${featureIndex > 0 ? 'mt-5' : 'mt-5'} bg-white p-4 rounded-lg border border-theme-border group/featured hover:border-theme-primary transition-all duration-200 hover:shadow-md`}
                      >
                        <div className="flex items-center mb-2">
                          {feature.icon &&
                            React.createElement(feature.icon, {
                              className: `w-5 h-5 ${feature.iconColor || 'text-yellow-500'} mr-2`,
                            })}
                          <h4 className="font-medium text-sm">{feature.title}</h4>
                        </div>
                        <p className="text-sm text-theme-text-muted mb-3 line-clamp-2">{feature.description}</p>
                        <Link
                          href={feature.href}
                          className="text-sm font-medium text-theme-primary flex items-center group-hover/featured:underline"
                        >
                          {feature.actionText || 'Learn more'} <ArrowRight className="w-3 h-3 ml-1" />
                        </Link>
                      </div>
                    ))}
                </div>
              ))}
            </div>

            {/* Footer */}
            {item.footer && (
              <div className="bg-gray-50 border-t border-theme-border p-3 flex flex-col sm:flex-row justify-between items-center">
                <span className="text-sm text-theme-text-muted">
                  <span className="text-gray-700 "> {item.footer.text} </span>
                  <Link
                    href={item.footer.linkHref}
                    className=" text-sm font-medium text-theme-primary inline-flex items-center hover:underline mt-2 sm:mt-0"
                  >
                    {item.footer.linkText}
                  </Link>
                </span>
                <Link
                  href={item.footer.viewAllHref}
                  className="text-sm font-medium text-theme-primary flex items-center hover:underline mt-2 sm:mt-0"
                >
                  {item.footer.viewAllText} <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuItem
